import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { File, Loader2, Plus } from "lucide-react";
import { Button } from "components/common/Button";
import { Dropdown } from "components/common/Dropdown";
import { Modal } from "components/common/Modal";
import { CurrentFolderHint } from "./CurrentFolderHint";
import { DestinationOptionLabel } from "./DestinationOptionLabel";
import { ROOT_DESTINATION } from "./constants";
import { toDestination, toFolderId } from "./utils";
import type { DropdownOption } from "components/common/Dropdown/types";
import type { MovePlanToFolderModalProps } from "./types";

export const MovePlanToFolderModal = ({
  planRecord,
  folderRecords,
  isCreatingFolder,
  isMoving,
  onClose,
  onCreateFolder,
  onMove,
}: MovePlanToFolderModalProps) => {
  const { t } = useTranslation();
  const newFolderInputId = useId();

  const [selectedDestination, setSelectedDestination] =
    useState(ROOT_DESTINATION);

  const [newFolderName, setNewFolderName] = useState("");

  const destinationOptions = useMemo<DropdownOption<string>[]>(
    () => [
      {
        value: ROOT_DESTINATION,
        label: (
          <DestinationOptionLabel name={t("smartSurveyor.folders.noFolder")} />
        ),
      },
      ...folderRecords.map((folder) => ({
        value: toDestination(folder.id),
        label: <DestinationOptionLabel name={folder.name} />,
      })),
    ],
    [folderRecords, t],
  );

  // A folderId with no loaded folder renders at the root, so it counts as "No folder" here too.
  const currentFolder = useMemo(
    () =>
      folderRecords.find((folder) => folder.id === planRecord?.folderId) ??
      null,
    [folderRecords, planRecord],
  );

  const isOpen = planRecord !== null;
  const isBusy = isCreatingFolder || isMoving;
  const currentDestination = toDestination(currentFolder?.id ?? null);
  const trimmedNewFolderName = newFolderName.trim();
  const isCreateDisabled = isBusy || trimmedNewFolderName === "";
  const isMoveDisabled = isBusy || selectedDestination === currentDestination;

  const handleCreateFolder = async () => {
    if (isCreateDisabled) return;
    const createdFolderId = await onCreateFolder(trimmedNewFolderName);
    if (createdFolderId === null) return;
    setSelectedDestination(toDestination(createdFolderId));
    setNewFolderName("");
  };

  const handleNewFolderKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") return;
    await handleCreateFolder();
  };

  const handleMove = () => {
    if (isMoveDisabled) return;
    onMove(toFolderId(selectedDestination));
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedDestination(currentDestination);
      setNewFolderName("");
    }
  }, [isOpen, currentDestination]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      disableBackdropClose={isBusy}
      disableEscapeClose={isBusy}
    >
      <div className="flex flex-col gap-5">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("smartSurveyor.folders.moveTitle")}
        </h2>
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-sm font-medium text-gray-700">
            {t("smartSurveyor.folders.floorPlan")}
          </span>
          <div className="flex min-h-10 min-w-0 items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <File size={16} className="mt-0.5 flex-shrink-0 text-gray-500" />
            {/* break-all: plan names are often one long token with underscores and no spaces. */}
            <span className="min-w-0 break-all text-sm text-gray-700">
              {planRecord?.name}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-gray-700">
            {t("smartSurveyor.folders.destination")}
          </span>
          <Dropdown<string>
            options={destinationOptions}
            value={selectedDestination}
            onChange={setSelectedDestination}
            disabled={isBusy}
            className="w-full"
            buttonClassName="h-10 w-full justify-between rounded-lg border-gray-300 px-3"
          />
          <CurrentFolderHint currentFolderName={currentFolder?.name ?? null} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={newFolderInputId}
            className="text-sm font-medium text-gray-700"
          >
            {t("smartSurveyor.folders.newFolder")}
          </label>
          <div className="flex gap-2">
            <input
              id={newFolderInputId}
              type="text"
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.target.value)}
              onKeyDown={handleNewFolderKeyDown}
              disabled={isBusy}
              className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              placeholder={t("smartSurveyor.folders.newFolderPlaceholder")}
            />
            <Button
              variant="outline"
              className="h-10"
              onClick={handleCreateFolder}
              disabled={isCreateDisabled}
              startAdornment={
                isCreatingFolder ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Plus size={14} />
                )
              }
            >
              {t("smartSurveyor.folders.createFolder")}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <Button
          size="lg"
          className="flex-1"
          onClick={onClose}
          disabled={isBusy}
        >
          {t("smartSurveyor.common.cancel")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={handleMove}
          disabled={isMoveDisabled}
          startAdornment={
            isMoving ? <Loader2 size={14} className="animate-spin" /> : null
          }
        >
          {t("smartSurveyor.folders.move")}
        </Button>
      </div>
    </Modal>
  );
};
